import { useState, useRef } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import { type ProjectItem } from "./ProjectDetailsForm";

function parseJsonArray(jsonStr: string | undefined): any[] {
  try {
    return jsonStr ? JSON.parse(jsonStr) : [];
  } catch {
    return [];
  }
}

const createDefaultProject = (totalCount: number): Partial<ProjectItem> => ({
  id: `project-${Date.now()}`,
  title_en: "",
  title_tr: "",
  date: new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
  version: "v1.0.0",
  tags_en: "[]",
  tags_tr: "[]",
  summary_en: "",
  summary_tr: "",
  detailed_content_en: "",
  detailed_content_tr: "",
  tech_stack: "",
  links: "[]",
  images: "[]",
  metrics: "[]",
  sort_order: totalCount
});

export function useProjectDraft(
  editingProject: Partial<ProjectItem> | null,
  setEditingProject: React.Dispatch<React.SetStateAction<Partial<ProjectItem> | null>>,
  projects: ProjectItem[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>,
  onRefresh: () => void
) {
  const handleSaveDraft = async () => {
    if (!editingProject?.title_en || !editingProject?.summary_en) {
      toast.error("English Title and Summary are required for draft!");
      return;
    }
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "project",
        item_id: editingProject.id,
        draft_data: editingProject
      });
      setActiveDraft(editingProject);
      toast.success("Project draft saved to staging successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingProject?.id) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", { operation: "delete", item_type: "project", item_id: editingProject.id });
      setActiveDraft(null);
      const orig = projects.find((p) => p.id === editingProject.id);
      if (orig) setEditingProject(orig);
      toast.success("Staging draft discarded.");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

async function uploadProjectImage(
  file: File,
  setUploading: (u: boolean) => void,
  setEditingProject: any
) {
  setUploading(true);
  try {
    const token = localStorage.getItem("portfolio_admin_token");
    const res = await fetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
      },
      body: file
    });
    const data = (await res.json()) as any;
    if (res.ok && data.success) {
      setEditingProject((p: any) => ({ ...p!, images: JSON.stringify([...parseJsonArray(p?.images), data.url]) }));
      toast.success("Image uploaded successfully!");
    } else {
      toast.error(data.error || "Upload failed");
    }
  } catch {
    toast.error("Failed to upload image");
  } finally {
    setUploading(false);
  }
}

export function useAdminProjects(projects: ProjectItem[], onRefresh: () => void) {
  const sortedProjects = [...projects].sort((a, b) => a.sort_order - b.sort_order);
  const [editingProject, setEditingProject] = useState<Partial<ProjectItem> | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activeDraft, setActiveDraft] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { handleSaveDraft, handleDiscardDraft } = useProjectDraft(
    editingProject,
    setEditingProject,
    projects,
    activeDraft,
    setActiveDraft,
    onRefresh
  );

  const startAdd = () => {
    setEditingProject(createDefaultProject(projects.length));
    setActiveDraft(null);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadProjectImage(file, setUploading, setEditingProject);
  };

  const handleSave = async () => {
    if (!editingProject?.title_en || !editingProject?.summary_en || !editingProject?.detailed_content_en) {
      toast.error("English Title, Summary, and Detailed Content are required!");
      return;
    }
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "project",
        item_id: editingProject.id,
        draft_data: editingProject
      });
      toast.success("Project published live successfully!");
      setActiveDraft(null);
      setEditingProject(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save project");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    try {
      await adminFetch("/api/admin/projects", { operation: "delete", id });
      toast.success("Project deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete project");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= sortedProjects.length) return;
    const itemA = sortedProjects[index];
    const itemB = sortedProjects[swapIdx];
    if (!itemA || !itemB) return;
    try {
      await adminFetch("/api/admin/projects", { ...itemA, sort_order: itemB.sort_order });
      await adminFetch("/api/admin/projects", { ...itemB, sort_order: itemA.sort_order });
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to reorder items");
    }
  };

  return {
    sortedProjects,
    editingProject,
    setEditingProject,
    uploading,
    fileInputRef,
    startAdd,
    handleImageUpload,
    handleSave,
    handleDelete,
    handleMove,
    activeDraft,
    setActiveDraft,
    handleSaveDraft,
    handleDiscardDraft
  };
}

export default useAdminProjects;
